<mat-expansion-panel [expanded]="initialExpanded" (opened)="toggleExpanded()" (closed)="toggleExpanded()">
  <mat-expansion-panel-header>
    <mat-panel-title>
      LAB ORDERS
      <button mat-icon-button class="monitoring-icon" (click)="openMonitoringDialog($event)"
        matTooltip="View Continuous Monitoring" aria-label="Open continuous monitoring">
        <mat-icon>monitor_heart</mat-icon>
      </button>
    </mat-panel-title>
    <!--
        Role filter. Rendered ONLY when a loaded order actually names a role: no order carries
        `vendorDoctors` today, and an always-empty menu is worse than no menu.
        Same interaction as patient-clinical-timeline's filter, in the same place — in the
        expansion-panel header, with stopPropagation so opening the menu does not toggle the
        panel and ticking a second role does not close the menu.
    -->
    <ng-container *ngIf="vendorDoctorRoleOptions.length">
      <button mat-icon-button class="role-filter-button" [matMenuTriggerFor]="roleFilterMenu"
        (click)="$event.stopPropagation()"
        [matTooltip]="selectedVendorDoctorRoles.length
            ? 'Filtering by ' + selectedVendorDoctorRoles.length + ' role(s)'
            : 'Filter by vendor doctor role'"
        aria-label="Filter lab orders by vendor doctor role">
        <mat-icon>filter_list</mat-icon>
        <span class="role-filter-count" *ngIf="selectedVendorDoctorRoles.length">{{
          selectedVendorDoctorRoles.length }}</span>
      </button>
      <mat-menu #roleFilterMenu="matMenu" class="filter-menu">
        <div mat-menu-content class="filter-menu-content" (click)="$event.stopPropagation()">
          <div class="filter-options">
            <h4>Vendor doctor role</h4>
            <mat-checkbox *ngFor="let role of vendorDoctorRoleOptions"
              [checked]="isVendorDoctorRoleSelected(role)"
              (change)="toggleVendorDoctorRole(role, $event.checked)" (click)="$event.stopPropagation()">
              {{ role }}
            </mat-checkbox>
            <button mat-button type="button" class="role-filter-clear"
              *ngIf="selectedVendorDoctorRoles.length"
              (click)="clearVendorDoctorRoles(); $event.stopPropagation()">
              Clear roles
            </button>
          </div>
        </div>
      </mat-menu>
    </ng-container>
  </mat-expansion-panel-header>

  <!--
      ONE definition of the badge row, used by all three sections.
      `badges.length` is false for an order whose `vendorDoctors` is null (every pre-existing
      order), for one whose only party is our own platform code, and for one whose slots carry
      no role — and in each case NOTHING is emitted: no container, no label, no space.
  -->
  <ng-template #vendorDoctorBadgeRow let-order>
    <ng-container *ngIf="vendorDoctorBadgesFor(order) as badges">
      <div class="vendor-doctor-badges" *ngIf="badges.length">
        <span class="vendor-doctor-badge" *ngFor="let badge of badges" [matTooltip]="badge.tooltip">
          <span class="vendor-doctor-badge-label">{{ badge.label }}</span>
          <span class="vendor-doctor-badge-role">{{ badge.role }}</span>
        </span>
      </div>
    </ng-container>
  </ng-template>

  <div class="clinical-timeline-info">

    <!-- Completed Lab Orders Section -->
    <div class="list-header expandable active-header" (click)="toggleHeader('completed')">
      Completed ({{ completedLabOrdersDisplay.length }})
      <mat-icon aria-hidden="false" aria-label="Expand/Collapse">
        {{ expandedHeader === 'completed' ? 'expand_less' : 'expand_more' }}
      </mat-icon>
    </div>
    <div *ngIf="expandedHeader === 'completed'" class="full-width">
      <ng-container *ngIf="completedLabOrdersDisplay; else listLoading">
        <ng-container *ngIf="completedLabOrdersDisplay.length; else noResults">
          <ng-container *ngFor="let group of groupedCompletedOrders">
            <div class="list-header">
              {{ group.date | date: 'longDate' }}
            </div>
            <mat-list class="full-width">
              <ng-container *ngFor="let order of group.orders; let last = last">
                <mat-list-item class="auto-height">
                  <div class="lab-order-item">
                    <div class="order-info">
                      <div class="order-header">
                        <span class="order-number">#{{ order.orderNumber || order.id.substring(0, 8) }}</span>
                        <span class="status-chip" [ngClass]="order.status">{{ order.status | titlecase }}</span>
                        <span class="review-status" [ngClass]="order.displayReviewStatus"
                          *ngIf="order.displayReviewStatus && order.labResults">
                          {{ order.displayReviewStatus | titlecase }}
                        </span>
                      </div>
                      <div class="order-details">
                        {{ order.testCount }} test(s)
                        <span class="abnormal-results-indicator" *ngIf="order.hasAbnormalResults">
                          <mat-icon class="warning-icon-small">warning</mat-icon>
                          {{ order.abnormalResultsCount }} abnormal
                        </span>
                      </div>
                      <div class="order-details">
                        {{ order.testNames }}
                      </div>
                      <ng-container
                        *ngTemplateOutlet="vendorDoctorBadgeRow; context: { $implicit: order }"></ng-container>
                      <div class="order-meta" *ngIf="order.labProvider">
                        <div class="provider-info">
                          <!-- <span class="provider-label">Provider:</span> -->
                          <img *ngIf="getProviderLogo(order.labProvider)"
                            [src]="'data:image/png;base64,' + getProviderLogo(order.labProvider)"
                            alt="{{ order.labProvider }} logo" class="provider-logo">
                          <!-- <span class="provider-name">{{ order.labProvider }}</span> -->
                        </div>
                      </div>
                      <div class="order-meta" *ngIf="order.notes">
                        Notes: {{ order.notes }}
                      </div>
                      <div class="order-meta" *ngIf="order.resultSummary">
                        Results: {{ order.resultSummary }}
                      </div>
                      <div class="order-meta" *ngIf="order.resultDate">
                        Completed: {{ order.resultDate | date: 'yyyy-MM-dd HH:mm' }}
                      </div>
                      <div class="order-meta" *ngIf="order.reviewedAt">
                        Reviewed: {{ order.reviewedAt | date: 'yyyy-MM-dd HH:mm' }}
                      </div>
                      <div class="order-meta">
                        Created By: {{ order.createdBy }} at {{ order.createdAt | date: 'yyyy-MM-dd HH:mm' }}
                      </div>
                    </div>
                    <div class="order-actions">
                      <button mat-icon-button (click)="viewLabResults(order)" class="menu-button">
                        <mat-icon>visibility</mat-icon>
                      </button>
                    </div>
                  </div>
                </mat-list-item>
                <mat-divider *ngIf="!last"></mat-divider>
              </ng-container>
            </mat-list>
          </ng-container>
        </ng-container>
      </ng-container>
    </div>

    <!-- Pending Lab Orders Section -->
    <div class="list-header expandable inactive-header" (click)="toggleHeader('pending')">
      Pending ({{ pendingLabOrdersDisplay.length }})
      <mat-icon aria-hidden="false" aria-label="Expand/Collapse">
        {{ expandedHeader === 'pending' ? 'expand_less' : 'expand_more' }}
      </mat-icon>
    </div>
    <div *ngIf="expandedHeader === 'pending'" class="full-width">
      <ng-container *ngIf="pendingLabOrdersDisplay; else listLoading">
        <ng-container *ngIf="pendingLabOrdersDisplay.length; else noResults">
          <ng-container *ngFor="let group of groupedPendingOrders">
            <div class="list-header">
              {{ group.date | date: 'longDate' }}
            </div>
            <mat-list class="full-width">
              <ng-container *ngFor="let order of group.orders; let last = last">
                <mat-list-item class="auto-height">
                  <div class="lab-order-item">
                    <div class="order-info">
                      <div class="order-header">
                        <span class="order-number">#{{ order.orderNumber || order.id.substring(0, 8) }}</span>
                        <span class="status-chip" [ngClass]="order.status">{{ order.status | titlecase }}</span>
                        <span class="priority-indicator" [ngClass]="order.priority"
                          *ngIf="order.priority !== 'routine'"></span>
                      </div>
                      <div class="order-details">
                        {{ order.testCount }} test(s)
                      </div>
                      <div class="order-details">
                        {{ order.testNames }}
                      </div>
                      <ng-container
                        *ngTemplateOutlet="vendorDoctorBadgeRow; context: { $implicit: order }"></ng-container>
                      <div class="order-meta" *ngIf="order.labProvider">
                        <div class="provider-info">
                          <!--  <span class="provider-label">Provider:</span> -->
                          <img *ngIf="getProviderLogo(order.labProvider)"
                            [src]="'data:image/png;base64,' + getProviderLogo(order.labProvider)"
                            alt="{{ order.labProvider }} logo" class="provider-logo">
                          <!--  <span class="provider-name">{{ order.labProvider }}</span> -->
                        </div>
                      </div>
                      <div class="order-meta" *ngIf="order.notes">
                        Notes: {{ order.notes }}
                      </div>
                      <div class="order-meta">
                        Created By: {{ order.createdBy }} at {{ order.createdAt | date: 'yyyy-MM-dd HH:mm' }}
                      </div>
                    </div>
                    <div class="order-actions">
                      <!-- Opened programmatically, not via appWhatsappDialogTrigger: the requisition
                           URL is short-lived and may need rendering, so it cannot be an @Input. -->
                      <button mat-icon-button (click)="sendLabOrderViaWhatsApp(order)"
                        matTooltip="Send lab request form via WhatsApp"
                        *ngIf="!stateUnit && patient && order.labTests && order.labTests.length > 0">
                        <mat-icon svgIcon="whatsapp"></mat-icon>
                      </button>
                      <button mat-icon-button (click)="downloadLabOrderRequisition(order)"
                        matTooltip="Download lab request form"
                        *ngIf="patient && order.labTests && order.labTests.length > 0">
                        <mat-icon>download</mat-icon>
                      </button>
                      <button mat-icon-button [matMenuTriggerFor]="orderMenu" class="menu-button">
                        <mat-icon>more_vert</mat-icon>
                      </button>
                      <mat-menu #orderMenu="matMenu">
                        <!--  <button mat-menu-item (click)="viewLabResults(order)">
                          <mat-icon>science</mat-icon>
                          <span>View Results</span>
                        </button> -->
                        <button mat-menu-item (click)="editLabOrder(order)" *ngIf="!stateUnit">
                          <mat-icon>edit</mat-icon>
                          <span>Edit</span>
                        </button>
                        <button mat-menu-item (click)="deleteLabOrder(order)" class="delete-action">
                          <mat-icon>delete</mat-icon>
                          <span>Delete</span>
                        </button>
                      </mat-menu>
                    </div>
                  </div>
                </mat-list-item>
                <mat-divider *ngIf="!last"></mat-divider>
              </ng-container>
            </mat-list>
          </ng-container>
        </ng-container>
      </ng-container>
    </div>

    <!-- Failed Lab Orders Section -->
    <div class="list-header expandable failed-header" (click)="toggleHeader('failed')">
      Failed ({{ failedLabOrdersDisplay.length }})
      <mat-icon aria-hidden="false" aria-label="Expand/Collapse">
        {{ expandedHeader === 'failed' ? 'expand_less' : 'expand_more' }}
      </mat-icon>
    </div>
    <div *ngIf="expandedHeader === 'failed'" class="full-width">
      <ng-container *ngIf="failedLabOrdersDisplay; else listLoading">
        <ng-container *ngIf="failedLabOrdersDisplay.length; else noResults">
          <ng-container *ngFor="let group of groupedFailedOrders">
            <div class="list-header">
              {{ group.date | date: 'longDate' }}
            </div>
            <mat-list class="full-width">
              <ng-container *ngFor="let order of group.orders; let last = last">
                <mat-list-item class="auto-height">
                  <div class="lab-order-item">
                    <div class="order-info">
                      <div class="order-header">
                        <span class="order-number">#{{ order.orderNumber || order.id.substring(0, 8) }}</span>
                        <span class="status-chip" [ngClass]="order.status">{{ order.status | titlecase }}</span>
                        <span class="priority-indicator" [ngClass]="order.priority"
                          *ngIf="order.priority !== 'routine'"></span>
                      </div>
                      <div class="order-details">
                        {{ order.testCount }} test(s)
                      </div>
                      <div class="order-details">
                        {{ order.testNames }}
                      </div>
                      <ng-container
                        *ngTemplateOutlet="vendorDoctorBadgeRow; context: { $implicit: order }"></ng-container>
                      <div class="order-meta error-message" *ngIf="order.errorMessage">
                        <mat-icon class="error-icon">error</mat-icon>
                        Error: {{ order.errorMessage }}
                      </div>
                      <div class="order-meta" *ngIf="order.labProvider">
                        <div class="provider-info">
                          <img *ngIf="getProviderLogo(order.labProvider)"
                            [src]="'data:image/png;base64,' + getProviderLogo(order.labProvider)"
                            alt="{{ order.labProvider }} logo" class="provider-logo">
                        </div>
                      </div>
                      <div class="order-meta" *ngIf="order.notes">
                        Notes: {{ order.notes }}
                      </div>
                      <div class="order-meta">
                        Created By: {{ order.createdBy }} at {{ order.createdAt | date: 'yyyy-MM-dd HH:mm' }}
                      </div>
                    </div>
                    <div class="order-actions">
                      <button mat-icon-button (click)="sendLabOrderViaWhatsApp(order, true)"
                        matTooltip="Send lab request form via WhatsApp"
                        *ngIf="!stateUnit && patient && order.labTests && order.labTests.length > 0">
                        <mat-icon svgIcon="whatsapp"></mat-icon>
                      </button>
                      <button mat-icon-button (click)="downloadLabOrderRequisition(order)"
                        matTooltip="Download lab request form"
                        *ngIf="patient && order.labTests && order.labTests.length > 0">
                        <mat-icon>download</mat-icon>
                      </button>
                      <button mat-icon-button [matMenuTriggerFor]="orderMenu" class="menu-button">
                        <mat-icon>more_vert</mat-icon>
                      </button>
                      <mat-menu #orderMenu="matMenu">
                        <!--  <button mat-menu-item (click)="viewLabResults(order)">
                          <mat-icon>science</mat-icon>
                          <span>View Results</span>
                        </button> -->
                        <button mat-menu-item (click)="resubmitLabOrder(order)" *ngIf="!stateUnit">
                          <mat-icon>refresh</mat-icon>
                          <span>Resubmit</span>
                        </button>
                        <button mat-menu-item (click)="editLabOrder(order)" *ngIf="!stateUnit">
                          <mat-icon>edit</mat-icon>
                          <span>Edit</span>
                        </button>
                        <button mat-menu-item (click)="deleteLabOrder(order)" class="delete-action">
                          <mat-icon>delete</mat-icon>
                          <span>Delete</span>
                        </button>
                      </mat-menu>
                    </div>
                  </div>
                </mat-list-item>
                <mat-divider *ngIf="!last"></mat-divider>
              </ng-container>
            </mat-list>
          </ng-container>
        </ng-container>
      </ng-container>
    </div>


  </div>
</mat-expansion-panel>

<ng-template #listLoading>
  <div class="list-loading">
    <mat-spinner color="primary" mode="indeterminate" diameter="20" style="float:right;"></mat-spinner>
  </div>
</ng-template>

<ng-template #noResults>
  <p class="no-records-message">No results found.</p>
</ng-template>